import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import api, { euro, fmtDate, apiError, pctIt } from "@/lib/api";
import { SectionTitle, StatusBadge } from "@/components/common";
import { Select } from "@/pages/Notizie";
import { toast } from "sonner";
import { FileText, Plus, ChevronDown, X, ExternalLink, Trash2, Ban } from "lucide-react";
import NuovaProposta from "@/components/NuovaProposta";
import { PropostaAzioni } from "@/components/PropostaAzioni";
import { useAuth } from "@/context/AuthContext";
import { useSticky } from "@/lib/sticky";
import { ProposteCestino } from "@/components/ProposteCestino";

const STATI = ["bozza", "firmata", "da_firmare", "presentata", "in_valutazione", "controproposta", "accettata", "rifiutata", "scaduta", "chiusa_positiva", "chiusa_negativa"];
const GRUPPI = [
  ["preparazione", "📝", "In preparazione", "bg-sky-50 border-sky-200 text-sky-700", "ring-sky-400"],
  ["attesa", "⏳", "In attesa di risposta", "bg-amber-50 border-amber-200 text-amber-700", "ring-amber-400"],
  ["controproposte", "🔁", "Controproposte", "bg-violet-50 border-violet-200 text-violet-700", "ring-violet-400"],
  ["subordinate", "🟠", "Accettate subordinate", "bg-orange-50 border-orange-200 text-orange-700", "ring-orange-400"],
  ["accettate", "✅", "Accettate", "bg-emerald-50 border-emerald-200 text-emerald-700", "ring-emerald-400"],
  ["archivio", "🗄️", "Archivio", "bg-slate-50 border-slate-200 text-slate-500", "ring-slate-400"],
];
const ASSEGNO = { custodia: "In custodia", consegnato: "Consegnato", restituito: "Restituito" };
const oggi = () => new Date().toISOString().slice(0, 10);

export const gruppoProposta = (p) => {
  const s = p.stato;
  if (["bozza", "da_firmare"].includes(s)) return "preparazione";
  if (["firmata", "presentata", "in_valutazione"].includes(s)) return "attesa";
  if (s === "controproposta") return "controproposte";
  if (["accettata", "chiusa_positiva"].includes(s) && !p.preliminare_annullato) return p.mutuo_fino_al && p.mutuo_fino_al >= oggi() ? "subordinate" : "accettate";
  return "archivio";
};

const fineGiorno = (d) => new Date(`${d.slice(0, 10)}T23:59:59`);
function countdown(d) {
  const ms = fineGiorno(d) - Date.now();
  if (ms <= 0) return { testo: "scaduta", ore: 0 };
  const ore = ms / 36e5, g = Math.floor(ore / 24), h = Math.floor(ore % 24);
  return { testo: g ? `${g}g ${h}h` : `${h}h ${Math.floor((ms % 36e5) / 6e4)}m`, ore };
}
const fmtDiff = (d) => (d == null ? "—" : `${d > 0 ? "+" : d < 0 ? "−" : ""}${pctIt(Math.abs(d))}`);
const diffPct = (p) => (p.importo && p.prezzo_richiesto ? Math.round(((p.importo - p.prezzo_richiesto) / p.prezzo_richiesto) * 1000) / 10 : null);

function Riquadri({ stats, open, setOpen }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-6 gap-3 mb-4" data-testid="proposte-riquadri">
      {GRUPPI.map(([k, ico, label, cls, ring]) => {
        const s = stats[k] || { n: 0, valore: 0, provv: 0 };
        return (
          <button key={k} data-testid={`prop-box-${k}`} aria-expanded={open === k} onClick={() => setOpen(open === k ? null : k)}
            className={`rounded-2xl border-2 p-3 sm:p-4 2xl:p-3 text-left transition-transform hover:-translate-y-0.5 ${cls} ${open === k ? `ring-2 ${ring}` : ""}`}>
            <div className="flex items-start gap-1 text-xs 2xl:text-sm font-bold"><span className="text-sm shrink-0">{ico}</span> <span data-testid={`prop-titolo-${k}`} className="leading-tight min-w-0 [overflow-wrap:normal] [word-break:keep-all] hyphens-none">{label}</span>
              <ChevronDown size={14} className={`ml-auto shrink-0 mt-0.5 transition-transform ${open === k ? "rotate-180" : ""}`} /></div>
            <div className="mono text-3xl font-extrabold mt-2 text-[#111111]" data-testid={`prop-count-${k}`}>{s.n}</div>
            <div className="text-[11px] text-slate-600 mt-1" data-testid={`prop-valore-${k}`}><span className="mono font-semibold">{euro(s.valore)}</span> valore offerto</div>
            <div className="text-[11px] text-slate-600" data-testid={`prop-provv-${k}`}><span className="mono font-semibold">{euro(s.provv)}</span> provvigioni in gioco</div>
          </button>
        );
      })}
    </div>
  );
}

function Extra({ p, g }) {
  if (g === "attesa" && p.irrevocabile_fino_al) {
    const c = countdown(p.irrevocabile_fino_al);
    return <span data-testid={`prop-countdown-${p.id}`} className={c.ore < 48 ? "font-bold text-red-600" : ""}>Irrevocabile: {c.testo}</span>;
  }
  if (g === "subordinate" && p.mutuo_fino_al) return <span data-testid={`prop-mutuo-${p.id}`} className="font-semibold text-orange-700">Mutuo fino al {fmtDate(p.mutuo_fino_al)} · {countdown(p.mutuo_fino_al).testo}</span>;
  if (g === "accettate") return p.preliminare_id
    ? <Link to="/preliminari" onClick={(e) => e.stopPropagation()} data-testid={`prop-prelim-link-${p.id}`} className="inline-flex items-center gap-1 font-semibold text-blue-600 underline">Preliminare <ExternalLink size={11} /></Link>
    : <span className="text-[var(--muted)]">—</span>;
  return <span className="text-[var(--muted)]">—</span>;
}

const COLS = "md:grid md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.4fr)_minmax(0,1fr)_110px_110px_70px_100px_minmax(0,1.3fr)] md:gap-3 md:items-center";

function Riga({ p, g, onOpen }) {
  const d = diffPct(p);
  const rossa = g === "attesa" && p.irrevocabile_fino_al && countdown(p.irrevocabile_fino_al).ore < 48;
  return (
    <button data-testid={`proposta-${p.id}`} onClick={() => onOpen(p)}
      className={`w-full text-left px-4 py-3 border-t first:border-t-0 border-[var(--border)] text-sm transition-colors ${COLS} ${rossa ? "bg-red-50 hover:bg-red-100 border-l-4 border-l-red-500" : g === "archivio" ? "bg-slate-50/60 text-slate-500 hover:bg-slate-100" : "hover:bg-[#FC82ED]/5"}`}>
      <div className="font-semibold truncate">{p.immobile_indirizzo || p.immobile_titolo || "Immobile"}</div>
      <div className="truncate text-slate-600">{p.acquirente_nome || "—"}</div>
      <div className="truncate text-slate-600">{p.agente || "—"}</div>
      <div className="mono font-bold text-[#111111] md:text-right">{euro(p.importo)}</div>
      <div className="mono text-slate-500 md:text-right">{p.prezzo_richiesto ? euro(p.prezzo_richiesto) : "—"}</div>
      <div data-testid={`prop-diff-${p.id}`} className={`mono md:text-right ${d != null && d < 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtDiff(d)}</div>
      <div className="text-xs">{p.assegno ? ASSEGNO[p.assegno.stato] || p.assegno.stato : "—"}</div>
      <div className="text-xs flex items-center gap-2 flex-wrap mt-1 md:mt-0"><StatusBadge value={p.stato} /><Extra p={p} g={g} /></div>
    </button>
  );
}

function ElimAnnulla({ p, onClose, onDone }) {
  const g = gruppoProposta(p);
  if (g === "archivio") return null;
  const elimina = async () => {
    if (!window.confirm("Eliminare questa bozza? Va nel Cestino per 30 giorni (ripristinabile da Pulizia dati).")) return;
    try { await api.post(`/proposte/${p.id}/cestino`); toast.success("Bozza spostata nel Cestino"); onClose(); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const annulla = async () => {
    const motivo = window.prompt("Annullare la proposta? Andrà in Archivio (un eventuale preliminare viene annullato). Motivo:", "Proposta annullata");
    if (motivo === null) return;
    try { await api.post(`/proposte/${p.id}/annulla`, { motivo }); toast.success("Proposta annullata: è in Archivio"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex justify-end mb-3">
      {g === "preparazione"
        ? <button data-testid="proposta-elimina" onClick={elimina} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-300 text-red-600 bg-white text-xs font-semibold"><Trash2 size={13} /> Elimina bozza</button>
        : <button data-testid="proposta-annulla" onClick={annulla} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 bg-white text-xs font-semibold"><Ban size={13} /> Annulla proposta</button>}
    </div>
  );
}

function Kv({ k, v }) {
  return <div><div className="text-[11px] uppercase tracking-wide text-[var(--muted)]">{k}</div><div className="text-sm font-medium">{v ?? "—"}</div></div>;
}

function Dettaglio({ p, onClose, onDone, setStato }) {
  const d = diffPct(p);
  const [modifica, setModifica] = useState(false);
  const conModulo = p.modello_id || p.modulo;
  const valori = Object.entries(p.valori || {}).filter(([, v]) => v);
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="proposta-dettaglio">
      <div className="bg-white w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-4">
          <div className="flex-1"><div className="font-bold text-lg">{p.immobile_indirizzo || p.immobile_titolo || "Proposta"}</div>
            <div className="text-xs text-[var(--muted)]">{p.immobile_titolo} · {fmtDate(p.data)}</div></div>
          <StatusBadge value={p.stato} />
          <button data-testid="proposta-dettaglio-chiudi" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Kv k="Proponente" v={p.acquirente_nome} /><Kv k="Venditore" v={p.venditore} /><Kv k="Agente" v={p.agente} />
          <Kv k="Prezzo offerto" v={euro(p.importo)} /><Kv k="Prezzo richiesto" v={p.prezzo_richiesto ? euro(p.prezzo_richiesto) : null} />
          <Kv k="Differenza" v={d == null ? null : fmtDiff(d)} /><Kv k="Provvigioni in gioco" v={euro(p.provvigione_gioco)} />
          <Kv k="Assegno" v={p.assegno ? `${ASSEGNO[p.assegno.stato] || p.assegno.stato} · ${fmtDate(p.assegno.data)}` : null} />
          <Kv k="Irrevocabile fino al" v={p.irrevocabile_fino_al ? fmtDate(p.irrevocabile_fino_al) : null} />
          <Kv k="Condizione mutuo fino al" v={p.mutuo_fino_al ? fmtDate(p.mutuo_fino_al) : null} />
          <Kv k="Rogito entro" v={p.rogito_entro ? fmtDate(p.rogito_entro) : null} />
        </div>
        <div className="flex gap-3 flex-wrap mb-3 text-sm">
          {p.immobile_id && <Link to={`/immobili/${p.immobile_id}`} data-testid="proposta-apri-immobile" className="font-semibold text-blue-600 underline">Apri scheda immobile</Link>}
          {p.preliminare_id && <Link to="/preliminari" className="font-semibold text-blue-600 underline">Apri preliminare</Link>}
        </div>
        <ElimAnnulla p={p} onClose={onClose} onDone={onDone} />
        {!conModulo && <div className="mb-3"><Select testid={`proposta-stato-${p.id}`} label="Stato" value={p.stato} onChange={(e) => setStato(p, e.target.value)} opts={STATI} /></div>}
        {p.immobile_id && !p.in_cestino && <button data-testid={`proposta-modifica-${p.id}`} onClick={() => setModifica(true)} className="mb-3 px-4 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm">Modifica proposta</button>}
        {modifica && <NuovaProposta id={p.id} onClose={() => setModifica(false)} onDone={onDone} />}
        {conModulo && <PropostaAzioni p={p} onDone={onDone} />}
        {valori.length > 0 && (
          <details className="mt-4"><summary className="text-xs font-semibold cursor-pointer text-slate-600">Tutti i dati della proposta ({valori.length})</summary>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">{valori.map(([k, v]) => <div key={k} className="flex justify-between gap-2 border-b border-[var(--border)] py-1"><span className="text-[var(--muted)]">{k.replace(/_/g, " ")}</span><span className="text-right">{v}</span></div>)}</div>
          </details>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function Proposte() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuova, setNuova] = useState(false);
  const [open, setOpen] = useState("attesa");
  const [sel, setSel] = useState(null);
  const { monitoraggio: isDirezione, isDirettore, isSegreteria } = useAuth();
  const [agente, setAgente] = useSticky("proposte_agente", "");
  const load = () => api.get("/proposte").then((r) => { setItems(r.data); setLoading(false); setSel((s) => (s ? r.data.find((x) => x.id === s.id) || null : s)); });
  useEffect(() => { load(); }, []);

  const setStato = async (p, stato) => {
    await api.put(`/proposte/${p.id}`, { stato });
    toast.success(stato === "accettata" || stato === "chiusa_positiva" ? "Proposta chiusa — preliminare generato!" : "Aggiornata");
    load();
  };
  const agenti = [...items.filter((p) => p.owner_agent_id).reduce((m, p) => { if (!m.get(p.owner_agent_id) || m.get(p.owner_agent_id) === "—") m.set(p.owner_agent_id, p.agente || "—"); return m; }, new Map()).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const scoped = items.filter((p) => !agente || p.owner_agent_id === agente);
  const stats = scoped.reduce((a, p) => {
    const g = gruppoProposta(p); a[g] = a[g] || { n: 0, valore: 0, provv: 0 };
    a[g].n += 1; a[g].valore += Number(p.importo) || 0; a[g].provv += Number(p.provvigione_gioco) || 0; return a;
  }, {});
  const shown = open ? scoped.filter((p) => gruppoProposta(p) === open) : [];

  return (
    <div className="fade-up pb-24 md:pb-0" data-testid="proposte-page">
      <div className="flex items-start gap-3 flex-wrap mb-4">
        <div className="flex-1 min-w-[220px]"><SectionTitle sub="Bozza → Firmata dal proponente → Accettata. Con la condizione mutuo l'accettazione resta subordinata fino alla data indicata. All'accettazione si crea la scheda in Preliminari.">Proposte & Trattative</SectionTitle></div>
        <button data-testid="nuova-proposta-btn" onClick={() => setNuova(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm"><Plus size={16} /> Nuova proposta</button>
      </div>
      {nuova && <NuovaProposta onClose={() => setNuova(false)} onDone={load} />}
      {(isDirezione || isDirettore) && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-[var(--muted)]">Agente:</span>
          <select data-testid="proposte-filtro-agente" value={agente} onChange={(e) => setAgente(e.target.value)} className="px-3 py-2 rounded-xl border border-[var(--border)] text-base sm:text-sm bg-white">
            <option value="">Tutti gli agenti</option>
            {agenti.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
          </select>
        </div>
      )}
      <Riquadri stats={stats} open={open} setOpen={setOpen} />
      {loading ? <div className="skeleton h-40 rounded-2xl" /> :
      !open ? <div className="text-sm text-[var(--muted)] py-4" data-testid="proposte-hint">Apri un riquadro per vedere le proposte.</div> :
      shown.length === 0 ? <div className="card-immo p-12 text-center" data-testid="proposte-vuoto"><FileText className="mx-auto text-[var(--muted)] mb-3" size={32} /><p className="text-[var(--muted)] text-sm">Nessuna proposta in questo riquadro.</p></div> :
      <div className="card-immo overflow-hidden" data-testid={`proposte-elenco-${open}`}>
        <div className={`hidden ${COLS} px-4 py-2 text-[11px] uppercase tracking-wide text-[var(--muted)] font-semibold bg-[#F7F7F8]`}>
          <div>Immobile</div><div>Proponente</div><div>Agente</div><div className="text-right">Offerto</div><div className="text-right">Richiesto</div><div className="text-right">Diff.</div><div>Assegno</div><div>Stato</div>
        </div>
        {shown.map((p) => <Riga key={p.id} p={p} g={open} onOpen={setSel} />)}
      </div>}
      {(isDirezione || isSegreteria) && <ProposteCestino onRestored={load} />}
      {sel && <Dettaglio p={sel} onClose={() => setSel(null)} onDone={load} setStato={setStato} />}
    </div>
  );
}
